/**
 * MobileNavPopup — dropdown popup anchored beneath the FloatingTopBar.
 *
 * Purpose: Replaces the desktop sidebar's nav on mobile/tablet. Triggered from the
 *          top-bar hamburger; renders below the bar at the inline-start edge.
 * Flow: overlay catches outside clicks → popup fades + scales in → tapping a nav
 *       link, profile, settings, or sign-out closes it.
 *
 * Hidden at lg and above (desktop has the persistent Sidebar).
 */
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  LogOut,
  UserCircle,
  type LucideIcon,
} from "lucide-react";
import { UserAvatar } from "@/shared/components/UserAvatar";
import { cn } from "@/shared/lib/utils";

interface NavItem {
  href: string;
  icon: LucideIcon;
  labelKey: string;
  badge?: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

interface MobileNavPopupProps {
  open: boolean;
  onClose: () => void;
  userName: string;
  userEmail: string;
  userPhotoUrl?: string | null;
  navSections: NavSection[];
  onSignOut: () => void;
  isAdmin?: boolean;
}

export function MobileNavPopup({
  open,
  onClose,
  userName,
  userEmail,
  userPhotoUrl,
  navSections,
  onSignOut,
  isAdmin = false,
}: MobileNavPopupProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const profilePath = isAdmin ? "/admin/profile" : "/settings/profile";

  /**
   * Active when the path matches exactly or is a `/`-bounded child route.
   * Shared by the nav-section links and the Profile entry so the orange
   * active highlight stays consistent across both.
   */
  const isPathActive = (href: string) =>
    location.pathname === href ||
    (href !== "/" &&
      location.pathname.startsWith(href) &&
      location.pathname.charAt(href.length) === "/");

  const profileActive = isPathActive(profilePath);

  return (
    <>
      {/* Outside-click overlay */}
      <div
        className={cn(
          "fixed inset-0 z-[var(--z-scrim)] lg:hidden",
          open ? "pointer-events-auto" : "pointer-events-none",
        )}
        onClick={onClose}
        aria-hidden
      />

      {/* Popup panel */}
      <div
        className={cn(
          // Align under the FloatingTopBar: same start gap as the bar's
          // px-4/sm:px-6, and top = bar top inset (pt-4/sm:pt-6) + pill
          // height (3.25rem) + 1rem gap (mirrors the bell popover sideOffset).
          "fixed start-4 sm:start-6 z-[var(--z-modal)] lg:hidden",
          "top-[calc(1rem+3.25rem+1rem)] sm:top-[calc(1.5rem+3.25rem+1rem)]",
          "w-[15rem] rounded-[1.125rem] p-2",
          "bg-[var(--card)]",
          "transition-[opacity,transform,visibility] duration-200 ease-out",
          open
            ? "opacity-100 translate-y-0 scale-100 visible pointer-events-auto"
            : "opacity-0 -translate-y-2 scale-95 invisible pointer-events-none",
        )}
        style={{
          border: "1px solid rgba(26,25,23,0.09)",
          boxShadow:
            "0 8px 40px rgba(26,25,23,0.13), 0 2px 8px rgba(26,25,23,0.05)",
          transformOrigin: "top right",
        }}
        role="menu"
        inert={!open || undefined}
      >
        {/* User pill */}
        <div
          className="flex items-center gap-2.5 px-3 py-2.5 rounded-[0.625rem] mb-1.5"
          style={{ background: "var(--sidebar-active-bg)" }}
        >
          <UserAvatar
            name={userName}
            photoUrl={userPhotoUrl}
            sizeClass="size-7"
            textClass="text-[10px]"
          />
          <div className="flex flex-col min-w-0 text-start">
            <span
              className="text-[13px] font-semibold truncate capitalize"
              style={{ color: "var(--admin-text)" }}
            >
              {userName}
            </span>
            {userEmail && (
              <span
                className="text-[10px] truncate"
                style={{ color: "var(--admin-text-muted)" }}
              >
                {userEmail}
              </span>
            )}
          </div>
        </div>

        {/* Nav sections */}
        {navSections.map((section) => (
          <div key={section.label}>
            <p
              className="text-[9px] font-bold uppercase tracking-[0.16em] px-3 mt-3 mb-0.5"
              style={{ color: "var(--admin-text-muted)" }}
            >
              {t(section.label)}
            </p>
            {section.items.map((item) => {
              const isActive = isPathActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onClose}
                  className={cn(
                    "flex items-center gap-2.5 w-full text-start",
                    "px-3 py-2 rounded-[0.625rem] text-[13px] font-medium",
                    "transition-colors",
                    isActive
                      ? "font-semibold text-[var(--sidebar-text-active)]"
                      : "text-[var(--admin-text)] hover:bg-[var(--admin-bg)]",
                  )}
                  role="menuitem"
                >
                  <item.icon
                    className={cn(
                      "w-4 h-4 shrink-0",
                      isActive ? "opacity-100" : "opacity-60",
                    )}
                  />
                  <span className="flex-1 truncate">{t(item.labelKey)}</span>
                  {item.badge && (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white"
                      style={{ background: "var(--sidebar-text-active)" }}
                    >
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}

        {/* Profile entry */}
        <p
          className="text-[9px] font-bold uppercase tracking-[0.16em] px-3 mt-3 mb-0.5"
          style={{ color: "var(--admin-text-muted)" }}
        >
          {t("sidebar.account", { defaultValue: "Account" })}
        </p>
        <Link
          to={profilePath}
          onClick={onClose}
          className={cn(
            "flex items-center gap-2.5 w-full text-start",
            "px-3 py-2 rounded-[0.625rem] text-[13px] font-medium",
            "transition-colors",
            profileActive
              ? "font-semibold text-[var(--sidebar-text-active)]"
              : "text-[var(--admin-text)] hover:bg-[var(--admin-bg)]",
          )}
          role="menuitem"
        >
          <UserCircle
            className={cn(
              "w-4 h-4 shrink-0",
              profileActive ? "opacity-100" : "opacity-60",
            )}
          />
          <span className="flex-1 truncate">{t("sidebar.profile")}</span>
        </Link>

        {/* Divider */}
        <div
          className="h-px mx-1.5 my-1"
          style={{ background: "var(--border)" }}
          aria-hidden
        />

        {/* Sign out */}
        <button
          type="button"
          onClick={() => {
            onSignOut();
            onClose();
          }}
          className={cn(
            "flex items-center gap-2.5 w-full text-start",
            "px-3 py-2 rounded-[0.625rem] text-[13px] font-medium",
            "transition-colors",
            "hover:bg-[oklch(from_var(--sidebar-danger)_l_c_h_/_0.10)]",
          )}
          style={{ color: "var(--sidebar-danger)" }}
          role="menuitem"
        >
          <LogOut className="w-4 h-4 shrink-0 opacity-80" />
          <span className="flex-1 truncate">{t("sidebar.signOut")}</span>
        </button>
      </div>
    </>
  );
}
